import { historyJSON } from './editorHistory.js';
import { parseRecordedManualLength } from './manualLength.js';
import { siteBuildings, siteLevels } from './siteBuildings.js';
import { stairSpec } from './stairs.js';

export const FLOOR_ELEVATION_LIMITS = Object.freeze({ min: .1, maxAbove: 10000, maxBelow: 1000, minSeparation: .1 });
const L = FLOOR_ELEVATION_LIMITS;
const reviews = new WeakMap();
const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const finite = (value, min, max) => typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
const id = value => typeof value === 'string' && value.trim() === value && value.length > 0 && [...value].length <= 80;
const fail = message => { throw new Error(message); };

/** The signed value is relative to Site ground; the entered magnitude is
 * positive, including when a basement is below ground. This is an operator
 * assumption, not a LiDAR or laser measurement of a vertical datum. */
export function validateFloorElevationInput(record) {
  const required = ['version', 'source', 'basis', 'recordedFloorID', 'createdAt', 'side', 'metres', 'signedMetres'];
  if (!object(record) || !required.every(key => Object.hasOwn(record, key)) ||
      Object.keys(record).some(key => ![...required, 'manualInput'].includes(key)) ||
      record.version !== 1 || record.source !== 'operator-entered' || record.basis !== 'site-ground' ||
      !id(record.recordedFloorID) || !['above', 'below'].includes(record.side) ||
      !finite(record.metres, L.min, record.side === 'above' ? L.maxAbove : L.maxBelow) ||
      !finite(record.signedMetres, -L.maxBelow, L.maxAbove) ||
      Math.abs(record.signedMetres - (record.side === 'above' ? record.metres : -record.metres)) > 1e-9)
    fail('The original floor elevation input or Site-ground basis is invalid.');
  const stamp = typeof record.createdAt === 'string' && record.createdAt.length <= 40
    ? record.createdAt.match(/^(\d{4})-(\d{2})-(\d{2})T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/) : null;
  const year = Number(stamp?.[1]), month = Number(stamp?.[2]), day = Number(stamp?.[3]);
  const days = [31, year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (!stamp || month < 1 || month > 12 || day < 1 || day > days[month - 1] || !Number.isFinite(Date.parse(record.createdAt)))
    fail('The original floor elevation timestamp is invalid.');
  if (Object.hasOwn(record, 'manualInput')) {
    const parsed = parseRecordedManualLength(record.manualInput, { min: L.min, max: record.side === 'above' ? L.maxAbove : L.maxBelow });
    if (!parsed.ok || Math.abs(parsed.metres - record.metres) > 1e-9)
      fail('The floor elevation differs from its original manual-unit entry.');
  }
  return structuredClone(record);
}

/** Accept either an active plan or a siteLevels() row. The wrapper elevation
 * is authoritative for inactive floors; original input remains historical. */
export function floorElevationInfo(floor, floorID) {
  if (!object(floor)) fail('Choose an existing floor to review its elevation.');
  const plan = object(floor.plan) ? floor.plan : floor;
  const currentID = floorID ?? floor.id ?? plan.activeFloorId;
  const elevation = floor.elevation === undefined ? plan.elevation === undefined ? 0 : plan.elevation : floor.elevation;
  if (!id(currentID) || !finite(elevation, -100000, 100000)) fail('The existing floor elevation or identity needs review.');
  const record = plan.floorElevationInput === undefined ? null : validateFloorElevationInput(plan.floorElevationInput);
  return { elevation, source: !record ? 'unrecorded-plan-value' : record.recordedFloorID !== currentID ? 'different-floor' :
    Math.abs(elevation - record.signedMetres) > 1e-9 ? 'changed-after-entry' : 'operator-entered', ...(record ? { record } : {}) };
}

function canonicalLevels(doc) {
  if (!object(doc) || doc._siteLevels !== undefined || doc._groundSelections !== undefined || doc._sceneWalls !== undefined ||
      doc.activeFloorId === '@site-ground' || !id(doc.activeFloorId) || !Array.isArray(doc.floors) || doc.floors.length > 127 ||
      !Array.isArray(doc.stairs) || doc.stairs.length > 100 ||
      doc.elevation !== undefined && !finite(doc.elevation, -100000, 100000) ||
      doc.buildings !== undefined && (!Array.isArray(doc.buildings) || !doc.buildings.length || doc.buildings.length > 32))
    fail('Open an editable canonical Site with readable buildings, floors and stairs.');
  const buildings = siteBuildings(doc), buildingIDs = new Set();
  for (const building of buildings) {
    if (!object(building) || !id(building.id) || buildingIDs.has(building.id)) fail('The Site has ambiguous building ownership.');
    buildingIDs.add(building.id);
  }
  const levels = siteLevels(doc), floorIDs = new Set(), counts = new Map();
  for (const floor of levels) {
    if (!object(floor) || !id(floor.id) || floorIDs.has(floor.id) || !buildingIDs.has(floor.buildingId) ||
        !finite(floor.elevation, -100000, 100000) || !object(floor.plan) ||
        floor.plan.activeFloorId !== undefined && floor.plan.activeFloorId !== floor.id ||
        floor.plan.buildingId !== undefined && floor.plan.buildingId !== floor.buildingId)
      fail('The Site has an unreadable or ambiguously owned floor.');
    floorIDs.add(floor.id);
    counts.set(floor.buildingId, (counts.get(floor.buildingId) || 0) + 1);
    if (counts.get(floor.buildingId) > 32) fail('A building exceeds the supported floor review limit.');
  }
  return { buildings, levels };
}

/** Review one already selected non-ground floor. The operation never moves
 * geometry in Site X/Y or changes a floor-relative wall/camera height. */
export function reviewFloorElevation(doc, floorID, options = {}) {
  if (!object(options) || Object.keys(options).some(key => !['metres', 'side', 'manualInput'].includes(key)))
    fail('Enter a floor elevation manually; horizontal instrument readings are not vertical datum evidence.');
  const { buildings, levels } = canonicalLevels(doc);
  if (floorID !== doc.activeFloorId || !id(floorID)) fail('Select the floor you want to change before reviewing its elevation.');
  const target = levels.find(floor => floor.id === floorID);
  if (!target || !['above', 'below'].includes(options.side)) fail('Choose an existing upper floor or basement and its side of Site ground.');
  const building = buildings.find(item => item.id === target.buildingId);
  const siblings = levels.filter(floor => floor.buildingId === target.buildingId);
  const ground = siblings.filter(floor => Math.abs(floor.elevation) < .001);
  if (ground.length !== 1 || ground[0].id === floorID || Math.abs(target.elevation) < .001)
    fail('This building needs one fixed Site-ground floor at zero before another floor elevation can be reviewed.');
  const oldSide = target.elevation > 0 ? 'above' : 'below';
  if (options.side !== oldSide) fail('Keep this floor above or below Site ground. Moving it across ground needs a separate building review.');
  const record = validateFloorElevationInput({ version: 1, source: 'operator-entered', basis: 'site-ground', recordedFloorID: floorID,
    createdAt: new Date().toISOString(), side: options.side, metres: options.metres,
    signedMetres: options.side === 'above' ? options.metres : -options.metres,
    ...(Object.hasOwn(options, 'manualInput') ? { manualInput: options.manualInput } : {}) });
  for (const sibling of siblings) {
    if (sibling.id === floorID) continue;
    const before = target.elevation - sibling.elevation, after = record.signedMetres - sibling.elevation;
    if (Math.abs(before) < L.minSeparation - 1e-9) fail('Existing levels in this building are too close or have ambiguous order. Review them before moving this floor.');
    if (before * after <= 0 || Math.abs(after) < L.minSeparation - 1e-9)
      fail('The proposed elevation would cross or overlap another floor in this building. Keep their order and at least 0.1 m separation.');
  }
  const fingerprint = historyJSON(doc), beforeInfo = floorElevationInfo(target);
  const changed = structuredClone(doc); changed.elevation = record.signedMetres; changed.floorElevationInput = record; changed.review = null;
  const afterInfo = floorElevationInfo(changed);
  const stairChanges = [];
  for (const stair of doc.stairs) {
    if (!object(stair) || !id(stair.id)) fail('The Site has an unreadable stair identity.');
    if (stair.fromFloorId !== floorID && stair.toFloorId !== floorID) continue;
    const otherID = stair.fromFloorId === floorID ? stair.toFloorId : stair.fromFloorId;
    const other = levels.find(floor => floor.id === otherID);
    if (!other || other.buildingId !== target.buildingId) fail('A connected stair crosses buildings or refers to a missing floor. Review its connection first.');
    if (!finite(stair.run, .1, 1000)) fail('A connected stair has an invalid run. Review it before changing floor elevation.');
    const prior = stairSpec(doc, stair), next = stairSpec(changed, stair);
    if (!prior || !next) fail('The proposed elevation would invalidate a connected stair rise or its lower/upper order.');
    stairChanges.push({ id: stair.id, name: typeof stair.name === 'string' ? stair.name.slice(0, 120) : 'Stair',
      beforeRise: prior.rise, afterRise: next.rise, beforeRisers: prior.count, afterRisers: next.count,
      beforeTread: prior.tread, afterTread: next.tread });
  }
  const row = (floor, afterElevation = floor.elevation) => ({ floorID: floor.id, name: typeof floor.name === 'string' ? floor.name.slice(0, 120) : 'Floor',
    beforeElevation: floor.elevation, afterElevation, active: floor.id === floorID });
  const stack = siblings.map(floor => row(floor, floor.id === floorID ? record.signedMetres : floor.elevation))
    .sort((a, b) => a.beforeElevation - b.beforeElevation || a.floorID.localeCompare(b.floorID));
  const label = typeof target.name === 'string' ? target.name.slice(0, 120) : 'Floor';
  const common = { floorID, floorName: label, buildingID: target.buildingId,
    buildingName: typeof building.name === 'string' ? building.name.slice(0, 120) : 'Building' };
  const affected = { floors: 1, walls: target.plan.walls?.length ?? 0, rooms: target.plan.areas?.filter(area => area.kind === 'floor').length ?? 0,
    cameras: target.plan.cams?.length ?? 0, stairs: stairChanges.length };
  const warnings = ['This is an operator-entered height relative to Site ground, not an independently verified vertical measurement.',
    'Only this floor elevation changes. Its plan X/Y positions, floor-relative walls, cameras, roof values, original scans and other buildings stay unchanged.',
    'Inspect the resulting slabs, roof clearances, stair landings and camera coverage in 3D. This review does not solve volume collisions or certify stair construction.'];
  const review = { doc: changed,
    before: { ...common, elevation: target.elevation, ...beforeInfo },
    after: { ...common, elevation: record.signedMetres, ...afterInfo },
    stack, stairChanges, affected, warnings };
  reviews.set(review, { fingerprint, candidate: historyJSON(changed),
    presentation: historyJSON([review.before, review.after, review.stack, review.stairChanges, review.affected, review.warnings]) });
  return review;
}

export function applyFloorElevationReview(store, review) {
  const proof = reviews.get(review);
  if (!proof || !store || store._edit || historyJSON(store.doc) !== proof.fingerprint ||
      historyJSON(review.doc) !== proof.candidate ||
      historyJSON([review.before, review.after, review.stack, review.stairChanges, review.affected, review.warnings]) !== proof.presentation)
    fail('The Site or reviewed floor elevation changed. Finish editing and review the floor again.');
  const doc = structuredClone(review.doc);
  store.snapshot(); store.doc = doc; reviews.delete(review); store.touch();
  return doc;
}
